import { useState, useEffect, useCallback } from "react"
import { GetUserLicenses } from "aldoo-ra/Admin/ProductLicensing/api"
import { url } from "aldoo-ra/CMS/url"
import useSigninController from "aldoo-ra/Signin/signin-controller"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import { navigate } from "aldoo-ra/Navigate"
import {DocumentDuplicateIcon, LinkIcon } from "@heroicons/react/24/outline"

const AddOns = () => {
  const [userLicenses, setUserLicenses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { userProfile } = useSigninController()
  const { t } = Localization()
  const texts = TextBundle({ id: "add-ons" })

  // Load user's external licenses
  const loadUserLicenses = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await GetUserLicenses()
      
      if (response.result && response.data) {
        // Filter for external licenses only
        const externalLicenses = response.data.filter(license => 
          license.external === true || license.model?.external === true
        )
        setUserLicenses(externalLicenses)
      } else {
        setUserLicenses([])
      }
    } catch (error) {
      console.error('Error loading user licenses:', error)
      setUserLicenses([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Load licenses when component mounts and user profile is available
  useEffect(() => {
    if (userProfile) {
      loadUserLicenses()
    }
  }, [userProfile, loadUserLicenses])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />

          <Typography 
            variant="info_16" 
            className="text-text_secondary my-0"
          >
            {texts?.loading_addons || "Loading your add-ons..."}
          </Typography>
        </div>
      </div>
    )
  }

  // No external licenses found
  if (userLicenses.length === 0) {
    return (
      <div className="text-center py-12">
        <Typography 
          variant="h4" 
          className="text-text mb-4"
        >
          {texts?.no_addons_found || "No Add-ons Found"}
        </Typography>

        <Typography 
          variant="body_20" 
          className="text-text_secondary my-0"
        >
          {texts?.no_external_licenses_yet || "You don't have any external product licenses yet."}
        </Typography>
      </div>
    )
  }

  return (
    <div className="grid gap-4 md:gap-6">
      {userLicenses.map((license, index) => {
        
        return (
          <div
            key={license._id || index}
            className="w-full max-w-[460px] md:max-w-full flex flex-col md:flex-row items-center justify-between gap-4 bg-paper rounded-[24px] shadow-md transition-shadow px-4 py-6 md:p-6 mx-auto"
          >
            {/* Product Info with Logo */}
            <div className="flex items-center space-x-4">
              {/* Product Logo */}
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-paper rounded-lg border">
                {license.product?.thumbnail ? (
                  <img
                    src={url(license.product.thumbnail)}
                    alt={license.productName || 'Product'}
                    className="w-full h-full object-contain rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray_3 rounded flex items-center justify-center">
                    <Typography 
                      variant="info_14" 
                      className="text-text_secondary my-0"
                    >
                      {texts?.no_image || "No Image"}
                    </Typography>
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="flex-1">
                <Typography 
                  variant="body_24_accent" 
                  className="font-semibold my-0"
                >
                  {license.productName || license.productId || texts?.unknown_product || "Unknown Product"}
                </Typography>
                
                {license.product?.description && (
                  <Typography 
                    variant="info_16" 
                    className="text-gray_1 my-0"
                  >
                    {license.product.description}
                  </Typography>
                )}

                {license.validity && license.validity > 0 && (
                  <Typography 
                    variant="info_14" 
                    className="text-gray_1"
                  >
                    {t(texts?.license_validity || "Valid for {{daysNumber}} days", {
                      daysNumber: license.validity
                    })}
                  </Typography>
                )}
              </div>
            </div>

            {/* Right Side: Copy Button with License Code and Activate Button */}
            <div className="flex flex-col md:flex-row items-center md:items-start justify-center gap-3 md:gap-4">
              {/* Copy Button with License Code */}
              <div className="flex flex-col items-center">
                <Button
                  variant="secondary_s"
                  className="flex items-center gap-2"
                  onClick={() => {
                    if (license.code) {
                      navigator.clipboard.writeText(license.code)
                      showNotification({
                        message: texts?.copied_to_clipboard || "Copied to clipboard",
                        className: "bg-primary text-text_inv",
                      })
                    }
                  }}
                >
                  <DocumentDuplicateIcon className="w-5 h-auto flex-shrink-0" />

                  <Typography className="font-mono font-semibold text-sm whitespace-nowrap">
                    {license.code || texts?.not_available || 'N/A'}
                  </Typography>
                </Button>

                {/* License Code Label - Positioned below button */}
                <Typography
                  variant="info_12"
                  className="text-gray_1 whitespace-nowrap mt-2 mb-0"
                >
                  {texts?.license_code || "License Code"}
                </Typography>
              </div>

              {/* Activate Button */}
              {license.product?.meta?.activate_url && (
                <div className="flex flex-col items-center">
                  <Button
                    variant="primary_s"
                    onClick={() => {
                      navigate(license.product.meta.activate_url, "_blank")
                    }}
                    className="gap-2"
                  >
                    <LinkIcon className="w-5 h-auto"/>
                    {texts?.activate_cta || "Activate"}
                  </Button>

                  {/* Spacer to align with license code label */}
                  <Typography
                    variant="info_12"
                    className="hidden md:block text-gray_1 whitespace-nowrap mt-2 invisible"
                  >
                    &nbsp;
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default AddOns